import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  Menu, X, Home, Briefcase, Coins, Bell, Newspaper,
  MessageSquare, BarChart3, User, MessageCircle, LogOut,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import OasisMark from '../common/OasisMark';
import InstrumentSearch from './InstrumentSearch';

const NAV_LINKS = [
  { to: '/bist', label: 'BIST' },
  { to: '/us-stocks', label: 'US Hisse' },
  { to: '/crypto', label: 'Kripto' },
  { to: '/altin', label: 'Altın' },
  { to: '/all-stocks', label: 'Tüm Hisseler' },
  { to: '/news', label: 'Haberler' },
  { to: '/forum', label: 'Forum' },
  { to: '/ekonomik-takvim', label: 'Takvim' },
];

const USER_MENU_LINKS = [
  { to: '/', label: 'Panel', icon: Home },
  { to: '/portfolio', label: 'Portföy', icon: Briefcase },
  { to: '/crypto', label: 'Kripto', icon: Coins },
  { to: '/alerts', label: 'Uyarılar', icon: Bell },
  { to: '/news', label: 'Haberler', icon: Newspaper },
  { to: '/forum', label: 'Forum', icon: MessageSquare },
  { to: '/all-stocks', label: 'Tüm Hisseler', icon: BarChart3 },
  { to: '/profile', label: 'Profil', icon: User },
  { to: '/feedback', label: 'Bize Yazın', icon: MessageCircle },
];

const Logo: React.FC = () => (
  <Link to="/" className="flex items-center gap-2.5">
    <OasisMark />
    <span className="text-[15px] font-semibold tracking-tight select-none">
      <span className="text-white">Oasis</span>
      <span className="text-accent-400">Finans</span>
    </span>
  </Link>
);

const Header: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
      setIsDropdownOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-1.5 text-[13px] font-medium rounded-lg transition-colors ${
      isActive ? 'text-white bg-white/[0.07]' : 'text-gray-400 hover:text-white hover:bg-white/[0.05]'
    }`;

  return (
    <header className="sticky top-0 z-50 bg-canvas/80 backdrop-blur-xl border-b border-white/[0.06]">
      <div className="container-financial">
        <div className="flex items-center justify-between h-16">
          <Logo />

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink key={link.to} to={link.to} className={navLinkClass}>
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block w-64 xl:w-72 mx-3">
            <InstrumentSearch />
          </div>

          {/* Desktop Right */}
          <div className="hidden md:flex items-center gap-2">
            {!isAuthenticated ? (
              <>
                <Link to="/login" className="btn-ghost">Giriş Yap</Link>
                <Link to="/register" className="btn-accent">Kayıt Ol</Link>
              </>
            ) : (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="focus:outline-none focus:ring-2 focus:ring-accent-500/50 rounded-full"
                  aria-label="Account menu"
                  aria-expanded={isDropdownOpen}
                  aria-haspopup="true"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-500 to-accent-alt-600 flex items-center justify-center ring-1 ring-white/20 hover:ring-white/40 transition-all">
                    {user?.first_name ? (
                      <span className="font-semibold text-white text-sm select-none" aria-hidden="true">
                        {user.first_name.charAt(0)}
                      </span>
                    ) : (
                      <User className="h-4 w-4 text-white/80" aria-hidden="true" />
                    )}
                  </div>
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-xl bg-surface border border-white/[0.08] shadow-xl shadow-black/40 py-1.5 z-50">
                    <div className="px-3.5 py-2.5 border-b border-white/[0.06] mb-1">
                      <p className="text-[13px] font-medium text-white">
                        {user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.username || 'User'}
                      </p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email}</p>
                    </div>
                    {USER_MENU_LINKS.map((item) => (
                      <Link
                        key={item.to + item.label}
                        to={item.to}
                        onClick={() => setIsDropdownOpen(false)}
                        className="flex items-center gap-2.5 mx-1.5 px-2.5 py-2 text-[13px] text-gray-300 hover:text-white hover:bg-white/[0.05] rounded-lg transition-colors"
                      >
                        <item.icon className="h-4 w-4 text-gray-500" />
                        {item.label}
                      </Link>
                    ))}
                    <div className="border-t border-white/[0.06] mt-1 pt-1">
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2.5 w-full mx-1.5 px-2.5 py-2 text-[13px] text-danger-400 hover:bg-danger-400/10 rounded-lg transition-colors"
                      >
                        <LogOut className="h-4 w-4" />
                        Çıkış Yap
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-white/[0.06] pt-3 space-y-1 mobile-menu-panel">
            <div className="px-1 pb-2">
              <InstrumentSearch onNavigate={() => setIsMobileMenuOpen(false)} />
            </div>
            {!isAuthenticated ? (
              <div className="flex gap-2 px-1 pb-2">
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="btn-ghost flex-1 border border-white/[0.08]"
                >
                  Giriş Yap
                </Link>
                <Link
                  to="/register"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="btn-accent flex-1"
                >
                  Kayıt Ol
                </Link>
              </div>
            ) : (
              <div className="px-3 py-2.5 bg-white/[0.03] rounded-xl mb-2">
                <p className="text-[13px] font-medium text-white">
                  {user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.username || 'User'}
                </p>
                <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email}</p>
              </div>
            )}
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive ? 'text-white bg-white/[0.07]' : 'text-gray-400 hover:text-white hover:bg-white/[0.05]'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            {isAuthenticated && (
              <>
                <div className="border-t border-white/[0.06] my-2"></div>
                {USER_MENU_LINKS.filter(l => !NAV_LINKS.some(n => n.to === l.to)).map((item) => (
                  <Link
                    key={item.to + item.label}
                    to={item.to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-white/[0.05] rounded-lg transition-colors"
                  >
                    <item.icon className="h-4 w-4 text-gray-500" />
                    {item.label}
                  </Link>
                ))}
                <button
                  onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                  className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-danger-400 hover:bg-danger-400/10 rounded-lg transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Çıkış Yap
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
